<script setup lang="ts">
/**
 * Read-only code, JSON or a log. Pass `code` as text or `json` as a value
 * (pretty-printed with two spaces and lightly tinted: keys, strings, numbers,
 * literals). `language` adds a caption chip, `copyable` a copy button, and
 * `lineNumbers` a gutter.
 *
 * `maxHeight` caps the block and lets it scroll; `wrap` breaks long lines
 * instead of scrolling sideways. `autoScroll` follows new lines at the bottom —
 * for a live log — until the reader scrolls up, and picks up again once they
 * scroll back down.
 */
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';
import { NxCopyButton } from '../Copy';
import { formatJson, tokenizeJson } from './jsonTokens';

const props = withDefaults(
  defineProps<{
    code?: string;
    /** A value to show as pretty JSON; wins over `code`. */
    json?: unknown;
    /** Caption chip („JSON“, „Log“, „SQL“). */
    language?: string;
    /** Cap for the block's height (a CSS length or token); it scrolls beyond. */
    maxHeight?: string;
    wrap?: boolean;
    copyable?: boolean;
    lineNumbers?: boolean;
    /** Keeps the view at the bottom while new lines arrive. */
    autoScroll?: boolean;
    copyLabel?: string;
    copiedLabel?: string;
    /** Accessible name of the block. */
    ariaLabel?: string;
  }>(),
  {
    code: '',
    json: undefined,
    language: undefined,
    maxHeight: undefined,
    wrap: false,
    copyable: false,
    lineNumbers: false,
    autoScroll: false,
    copyLabel: 'Kopieren',
    copiedLabel: 'Kopiert',
    ariaLabel: undefined,
  }
);

const isJson = computed(() => props.json !== undefined);
const text = computed(() => (isJson.value ? formatJson(props.json) : (props.code ?? '')));
const tokens = computed(() => (isJson.value ? tokenizeJson(text.value) : []));
const lineCount = computed(() => text.value.split('\n').length);

const scrollRef = useTemplateRef<HTMLElement>('scroll');
/** The reader is at the bottom — only then does `autoScroll` follow. */
const pinned = ref(true);

function onScroll() {
  const el = scrollRef.value;
  if (!el) return;
  pinned.value = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
}

watch(
  text,
  async () => {
    if (!props.autoScroll || !pinned.value) return;
    await nextTick();
    const el = scrollRef.value;
    if (el) el.scrollTop = el.scrollHeight;
  },
  { immediate: true, flush: 'post' }
);
</script>

<template>
  <figure class="nx-code-block" :class="{ 'is-wrapped': props.wrap }" data-testid="nx-code-block">
    <figcaption v-if="props.language || props.copyable" class="nx-code-block__bar">
      <span v-if="props.language" class="nx-code-block__lang">{{ props.language }}</span>
      <NxCopyButton
        v-if="props.copyable"
        class="nx-code-block__copy"
        :value="text"
        :label="props.copyLabel"
        :copied-label="props.copiedLabel"
        icon-only
        height="xs"
        variant="ghost"
      />
    </figcaption>
    <div
      ref="scroll"
      class="nx-code-block__scroll"
      :style="props.maxHeight ? { maxHeight: props.maxHeight } : undefined"
      tabindex="0"
      role="region"
      :aria-label="props.ariaLabel ?? props.language"
      @scroll="onScroll"
    >
      <div class="nx-code-block__body">
        <span v-if="props.lineNumbers" class="nx-code-block__gutter" aria-hidden="true">
          <span v-for="n in lineCount" :key="n">{{ n }}</span>
        </span>
        <pre class="nx-code-block__pre"><code v-if="isJson"><span
          v-for="(t, i) in tokens"
          :key="i"
          :class="t.kind === 'plain' ? undefined : `tok-${t.kind}`"
        >{{ t.text }}</span></code><code v-else>{{ text }}</code></pre>
      </div>
    </div>
  </figure>
</template>

<style scoped>
.nx-code-block {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin: 0;
  overflow: hidden;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-md);
  background: var(--nxt-surface-sunken-bg);
}
.nx-code-block__bar {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  min-height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-2xs) 0 var(--nxt-space-sm);
  border-block-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
}
.nx-code-block__lang {
  color: var(--nxt-fg-muted);
  font-size: var(--nxt-font-size-xs);
  font-weight: var(--nxt-font-weight-semibold);
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.nx-code-block__copy {
  margin-inline-start: auto;
}
.nx-code-block__scroll {
  overflow: auto;
  overscroll-behavior: contain;
  outline: none;
}
.nx-code-block__scroll:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-code-block__body {
  display: flex;
  min-width: max-content;
  padding: var(--nxt-space-sm) var(--nxt-space-md);
}
.nx-code-block.is-wrapped .nx-code-block__body {
  min-width: 0;
}
.nx-code-block__gutter {
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  margin-inline-end: var(--nxt-space-md);
  padding-inline-end: var(--nxt-space-sm);
  border-inline-end: var(--nxt-hairline) solid var(--nxt-border-subtle);
  color: var(--nxt-fg-muted);
  text-align: end;
  user-select: none;
}
.nx-code-block__gutter,
.nx-code-block__pre {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: var(--nxt-font-size-xs);
  line-height: var(--nxt-line-height-normal);
  font-variant-numeric: tabular-nums;
}
.nx-code-block__pre {
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  color: var(--nxt-fg);
  white-space: pre;
  tab-size: 2;
}
.nx-code-block.is-wrapped .nx-code-block__pre {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.tok-key {
  color: var(--nxt-hue-sky-fg);
}
.tok-string {
  color: var(--nxt-hue-teal-fg);
}
.tok-number {
  color: var(--nxt-hue-amber-fg);
}
.tok-literal {
  color: var(--nxt-hue-violet-fg);
  font-weight: var(--nxt-font-weight-semibold);
}
.tok-punct {
  color: var(--nxt-fg-muted);
}
</style>
